/**
 * How a page remembers the way you last looked at it.
 *
 * Only settings worth outliving a page load belong here — which density,
 * which order. A search term or a filter is about right now; coming back
 * tomorrow to a list that silently shows four of twenty things is a bug
 * report, not a convenience. The caller decides which fields those are by
 * what it passes in.
 *
 * Storage can be absent (a browser with site data blocked throws on read)
 * and nothing depends on this having worked, so every path fails quietly.
 *
 * Who uses it: features/plant-list plantViews and features/locations
 * locationViews (which view the list is drawn in, and its order).
 *
 * @module viewMemory
 */

/**
 * Reads what was remembered under a key, keeping only the fields the caller
 * still recognises.
 *
 * @param key - The storage key, e.g. 'bloom.plantView'.
 * @param accept - Per field, a check that the stored value is still valid.
 * @returns The accepted fields, or an empty object.
 */
export function recallView<T extends object>(
  key: string,
  accept: { [K in keyof T]?: (value: unknown) => value is T[K] },
): Partial<T> {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const saved = JSON.parse(raw) as Record<string, unknown>;
    const out: Partial<T> = {};
    for (const field of Object.keys(accept) as Array<keyof T>) {
      const check = accept[field];
      const value = saved[field as string];
      if (check && check(value)) out[field] = value as T[typeof field];
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Remembers the given fields under a key.
 *
 * @param key - The storage key.
 * @param fields - Exactly what to keep; nothing else is written.
 */
export function rememberView(key: string, fields: Record<string, unknown>): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(fields));
  } catch {
    // Nothing depends on this having worked.
  }
}
